import { useState, type SyntheticEvent } from 'react';
import { useApi, useAuth } from '../auth.js';
import { MIN_PASSWORD_LENGTH, changePasswordError, userErrorText } from '../lib/users.js';

/** Change your own password (`POST /auth/password`); other sessions end. */
export function PasswordPage() {
  const api = useApi();
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const problem = changePasswordError({ current, next, confirm });
  // Only complain about the new password once something was typed there.
  const shown = next || confirm ? problem : null;

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(userErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="breadcrumbs">Settings /</div>
      <h1>Change password</h1>
      <p className="muted">
        Signed in as <span className="strong">{user?.email}</span>. The new password needs at least{' '}
        {MIN_PASSWORD_LENGTH} characters. Your other sessions (other browsers, devices) are signed
        out; this one stays.
      </p>
      {done && (
        <div className="card token-created" role="status">
          <p className="strong">Password changed. Other sessions were signed out.</p>
        </div>
      )}
      <form
        className="card token-form"
        onSubmit={(e) => {
          void submit(e);
        }}
      >
        <label className="field">
          <span>Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => {
              setCurrent(e.target.value);
              setDone(false);
            }}
          />
        </label>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            onChange={(e) => {
              setNext(e.target.value);
              setDone(false);
            }}
          />
        </label>
        <label className="field">
          <span>Repeat the new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setDone(false);
            }}
          />
        </label>
        {shown && <p className="form-error">{shown}</p>}
        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy || problem !== null}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
}
